import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { authApi, AuthTokens } from '../services/api/auth'

export interface User {
  id: number
  email: string
  display_name: string
  first_name?: string
  last_name?: string
  full_name?: string
  avatar_url?: string
  role: string // Primary role for backward compatibility
  authorities?: string[] // JHipster-style multiple authorities
  is_active: boolean
  is_verified: boolean
  is_superuser: boolean
  is_social_user: boolean
  // JHipster fields (optional)
  login?: string
  activated?: boolean
  lang_key?: string
  // Approval workflow
  approval_status?: 'PENDING' | 'APPROVED' | 'REJECTED'
  rejection_reason?: string | null
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: (credential: string) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const isAuthenticated = !!user

  // Initialize auth state
  useEffect(() => {
    initializeAuth()
  }, [])

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setIsLoading(false)
        return
      }

      // Try to get current user
      const userData = await authApi.getCurrentUser()
      setUser(userData)
    } catch (error) {
      // Token might be expired, try to refresh
      const refreshed = await refreshToken()
      if (!refreshed) {
        clearAuth()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      const response = await authApi.login({ email, password })
      
      // Drop any cached data from a previous session before we load this user's data.
      queryClient.clear()

      // Store tokens
      localStorage.setItem('access_token', response.access_token)
      localStorage.setItem('refresh_token', response.refresh_token)

      // Get user data
      const userData = await authApi.getCurrentUser()
      setUser(userData)

      toast.success('Welcome back!')
      navigate('/dashboard')
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Login failed'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithGoogle = async (credential: string) => {
    try {
      setIsLoading(true)
      const response = await authApi.googleAuth({
        provider: 'google',
        id_token: credential
      })

      // Drop any cached data from a previous session before we load this user's data.
      queryClient.clear()

      // Store tokens
      localStorage.setItem('access_token', response.access_token)
      localStorage.setItem('refresh_token', response.refresh_token)

      // Get user data
      const userData = await authApi.getCurrentUser()
      setUser(userData)

      if (userData.approval_status === 'PENDING') {
        toast.success('Sign-in successful — your account is awaiting approval')
        navigate('/pending-approval')
      } else if (userData.approval_status === 'REJECTED') {
        navigate('/account-rejected')
      } else {
        toast.success('Welcome!')
        navigate('/dashboard')
      }
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Google sign-in failed'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        await authApi.logout(refreshToken)
      }
    } catch (error) {
      // Ignore logout errors
      console.error('Logout error:', error)
    } finally {
      clearAuth()
      toast.success('Logged out successfully')
      navigate('/login')
    }
  }

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token')
      if (!refreshTokenValue) {
        return false
      }

      const response = await authApi.refreshToken(refreshTokenValue)
      
      // Update stored tokens
      localStorage.setItem('access_token', response.access_token)
      // Keep the same refresh token as returned by the API
      localStorage.setItem('refresh_token', response.refresh_token)

      // Get updated user data
      const userData = await authApi.getCurrentUser()
      setUser(userData)

      return true
    } catch (error) {
      clearAuth()
      return false
    }
  }

  const clearAuth = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    setUser(null)
    // Wipe per-user query cache so the next session doesn't see stale data.
    queryClient.clear()
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    loginWithGoogle,
    logout,
    refreshToken,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
